import React from "react";

import type { ScatterDataPoint } from "chart.js";
import type { DateTime } from "luxon";

export enum Locale {
  deAT,
  enAT,
  deDE,
  enDE,
  skSK,
  enSK,
  roRO,
  enRO,
  hrHR,
  enHR
}

export enum Environment {
  LOCAL,
  DEV,
  UAT,
  PROD,
}

export enum Application {
  ABU_ONLINE,
  ERSTE_REAL_ESTATE,
  DEFAULT,
}

export interface EndpointConfiguration {
  localUrl: string;
  devUrl: string;
  uatUrl: string;
  defaultProdUrl: string;
  abuOnlineUrl: string;
  ersteRealEstateUrl: string;
  currentApplication: Application;
  currentEnvironment: Environment;
}

// Response type from GEM_FUNDLIST API
type FundlistAPI = Array<{
  FUND_NAME_LONG: string;
  INVESTMENT_COMPANY_SHORT: string;
  SHARECLASS_CURRENCY?: string;
  SHARES: Array<{
    MASTER_SHARE_CERTIFICATE: boolean;
    SHARE_ISIN: string;
    SHARE_ISIN_DISPLAY: string;
    AS_I__ID: number;
    DAT_MIN: number;
    SHARE_CURRENCY?: string;
  }>;
}>;

type JSONFundlistAPI = {
  DATA: FundlistAPI;
};

// Response type from GEM_GRAPHICDATA_PERFORMANCE API
type GraphicDataPerformanceJson = FixedLengthArray<
  [
    {
      chart: {
        series: [
          {
            name: string;
            data: PerformanceDataJson;
          },
        ];
      };
    },
  ]
>;

type PerformanceDataJson = FixedLengthArray<
  [number, number, string, string, string, string, string, string, number]
>[];

type GraphicDataPerformance = {
  chart: {
    series: [
      {
        name: string;
        data: PerformanceData[];
      },
    ];
  };
};

interface PerformanceData {
  timestamp: number;
  performance: number;
  fee: number;
}

/**
 * Represents the JSON structure of the DDS group "GEM_FACTSHEET".
 *
 * Note: The dynamic data source response always returns an array even if there is only one element.
 *
 * @typedef {Object} JSONFactsheetAPI
 *
 * @property {Array<Object>} WARNINGS
 * - Contains an array of warning objects.
 *     - `ISIN` {string|null} - The International Securities Identification Number or null.
 *     - `WARNINGS` {Array<string>} - An array of warning messages.
 *
 * @property {Array<Object>} WARNINGS_META
 * - Contains an array of metadata for warnings.
 *     - `TITLE_MESSAGE` {string|null} - The title or message associated with the warnings, or null.
 *
 * @property {Array<Object>} ESMA_TEXT_ONLY
 * - Contains an array of objects with ESMA-related text.
 *     - `ESMA_TEXT_ONLY` {string|null} - The ESMA-specific text or null.
 */
type JSONFactsheetAPI = {
  WARNINGS: Array<{
    ISIN: string | null;
    WARNINGS: string[];
  }>;
  WARNINGS_META: Array<{
    TITLE_MESSAGE: string | null;
  }>;
  ESMA_TEXT_ONLY: Array<{
    ESMA_TEXT_ONLY: string | null;
  }>;
};

type Fund = {
  id: number;
  masterISIN: string;
  label: string;
  isins: string;
  investmentCompanyShort: string;
  currency: string;
} | null;

type TableData = {
  masterISIN: string;
  label: string;
  investmentCompanyShort: string;
  currency: string;
  finalAmount: number | null;
  totalPerformance: number | null;
};

type ShortWarning = {
  label: string;
  isHidden: boolean;
  message: React.ReactNode | null;
};

type ComplianceWarning = {
  masterISIN: string;
  warning: string[];
  title: string | null;
  esmaTextOnly: string | null;
};

type DepositData = {
  deposit: (number | ScatterDataPoint | null)[];
  dates: DateTime[];
};

// Fixed length array type implementation
type ArrayLengthMutationKeys = "splice" | "push" | "pop" | "shift" | "unshift" | number;
type ArrayItems<T extends Array<any>> = T extends Array<infer TItems> ? TItems : never; // eslint-disable-line @typescript-eslint/no-explicit-any
type FixedLengthArray<T extends any[]> = // eslint-disable-line @typescript-eslint/no-explicit-any
  Pick<T, Exclude<keyof T, ArrayLengthMutationKeys>> & {
    [Symbol.iterator]: () => IterableIterator<ArrayItems<T>>;
  };

export type {
  JSONFundlistAPI,
  GraphicDataPerformanceJson,
  PerformanceDataJson,
  JSONFactsheetAPI,
  Fund,
  TableData,
  ShortWarning,
  ComplianceWarning,
  FixedLengthArray,
  DepositData,
  GraphicDataPerformance,
  PerformanceData,
};
